import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useSnackbar } from 'notistack';
import {
  APIGetShippingCompanies,
  APIGetActiveShippingCompanies,
  APIGetShippingCompanyById,
  APICreateShippingCompany,
  APIUpdateShippingCompany,
  APIDeleteShippingCompany,
  IShippingCompany,
  ICreateShippingCompanyRequest,
  IUpdateShippingCompanyRequest
} from '../../service/api/APIShippingMaster';

// Query Keys
export enum EShippingMasterQueryKeys {
  GET_SHIPPING_COMPANIES = 'GET_SHIPPING_COMPANIES',
  GET_ACTIVE_SHIPPING_COMPANIES = 'GET_ACTIVE_SHIPPING_COMPANIES',
  GET_SHIPPING_COMPANY_BY_ID = 'GET_SHIPPING_COMPANY_BY_ID'
}

// Hook for getting all shipping companies
export const useGetShippingCompanies = (params?: {
  page?: number;
  limit?: number;
  status?: string;
  search?: string;
}) => {
  return useQuery<IShippingCompany[]>(
    [EShippingMasterQueryKeys.GET_SHIPPING_COMPANIES, params],
    () => APIGetShippingCompanies(params),
    {
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes
      keepPreviousData: true,
      onError: (error: any) => {
        console.error('❌ Failed to fetch shipping companies:', error);
      }
    }
  );
};

// Hook for getting only active shipping companies
export const useGetActiveShippingCompanies = () => {
  return useQuery<IShippingCompany[]>(
    [EShippingMasterQueryKeys.GET_ACTIVE_SHIPPING_COMPANIES],
    APIGetActiveShippingCompanies,
    {
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes
      onError: (error: any) => {
        console.error('❌ Failed to fetch active shipping companies:', error);
      }
    }
  );
};

// Hook for getting shipping company by ID
export const useGetShippingCompanyById = (companyUuid: string, enabled: boolean = true) => {
  return useQuery<IShippingCompany>(
    [EShippingMasterQueryKeys.GET_SHIPPING_COMPANY_BY_ID, companyUuid],
    () => APIGetShippingCompanyById(companyUuid),
    {
      enabled: enabled && !!companyUuid,
      staleTime: 2 * 60 * 1000, // 2 minutes
      cacheTime: 5 * 60 * 1000, // 5 minutes
      onError: (error: any) => {
        console.error('❌ Failed to fetch shipping company:', error);
      }
    }
  );
};

// Hook for creating shipping company
export const useCreateShippingCompany = () => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation<IShippingCompany, any, ICreateShippingCompanyRequest>(
    APICreateShippingCompany,
    {
      onSuccess: (data) => {
        enqueueSnackbar('✅ เพิ่มข้อมูลบริษัทขนส่งสำเร็จ!', { variant: 'success' });
        
        // Invalidate and refetch shipping companies list
        queryClient.invalidateQueries([EShippingMasterQueryKeys.GET_SHIPPING_COMPANIES]);
        
        console.log('✅ Shipping company created successfully:', data);
      },
      onError: (error: any) => {
        console.error('❌ Failed to create shipping company:', error);
        
        const errorMessage = error.response?.data?.message || error.message || 'เกิดข้อผิดพลาดในการเพิ่มข้อมูลบริษัทขนส่ง';
        enqueueSnackbar(`❌ ${errorMessage}`, { variant: 'error' });
      }
    }
  );
};

// Hook for updating shipping company
export const useUpdateShippingCompany = () => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation<IShippingCompany, any, { uuid: string; data: IUpdateShippingCompanyRequest }>(
    ({ uuid, data }) => APIUpdateShippingCompany(uuid, data),
    {
      onSuccess: (data, variables) => {
        enqueueSnackbar('✅ อัปเดตข้อมูลบริษัทขนส่งสำเร็จ!', { variant: 'success' });
        
        // Invalidate queries
        queryClient.invalidateQueries([EShippingMasterQueryKeys.GET_SHIPPING_COMPANIES]);
        queryClient.invalidateQueries([EShippingMasterQueryKeys.GET_SHIPPING_COMPANY_BY_ID, variables.uuid]);
        
        console.log('✅ Shipping company updated successfully:', data);
      },
      onError: (error: any) => {
        console.error('❌ Failed to update shipping company:', error);
        
        const errorMessage = error.response?.data?.message || error.message || 'เกิดข้อผิดพลาดในการอัปเดตข้อมูลบริษัทขนส่ง';
        enqueueSnackbar(`❌ ${errorMessage}`, { variant: 'error' });
      }
    }
  );
};

// Hook for deleting shipping company
export const useDeleteShippingCompany = () => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation<{ success: boolean; message?: string }, any, string>(
    APIDeleteShippingCompany,
    {
      onSuccess: (data) => {
        enqueueSnackbar('✅ ลบข้อมูลบริษัทขนส่งสำเร็จ!', { variant: 'success' });
        
        // Invalidate shipping companies list
        queryClient.invalidateQueries([EShippingMasterQueryKeys.GET_SHIPPING_COMPANIES]);
        
        console.log('✅ Shipping company deleted successfully:', data);
      },
      onError: (error: any) => {
        console.error('❌ Failed to delete shipping company:', error);
        
        const errorMessage = error.response?.data?.message || error.message || 'เกิดข้อผิดพลาดในการลบข้อมูลบริษัทขนส่ง';
        enqueueSnackbar(`❌ ${errorMessage}`, { variant: 'error' });
      }
    }
  );
};
